/**
 * Inspired by _.cond, this takes an array of entries of the form [condition, result]
 * It returns the result for the first truthy condition.
 *
 * This is called simpleCond because _.cond works with predicate and result functions,
 * but this just works on values. Note: this means that the results passed in will be
 * evaluated first.
 * @param {Array<Array<*, *>>} conditions
 *
 * @example ```
 *      function getWaterState(temp) {
 *          return simpleCond([
 *              [temp < 32, "Ice"],
 *              [temp >= 212, "Steam"],
 *              [true, "Water"]
 *          ]);
 *      }
 * ```
 * @returns
 */
export function simpleCond(conditions) {
    for (const entry of conditions) {
        if (!Array.isArray(entry)) throw new Error(`simpleCond: unexpected non-array entry: ${entry}`);
        const [condition, result] = entry;
        if (condition) return result;
    }
    return undefined;
}

export function ensureArray(arg) {
    return Array.isArray(arg) ? arg : [arg];
}

/**
 * Recursively freeze an object and all of its nested objects/arrays.
 * Functions are frozen as objects (their properties become immutable) but remain callable.
 * @template T
 * @param {T} obj
 * @returns {T}
 */
export function deepFreeze(obj) {
    Object.getOwnPropertyNames(obj).forEach((name) => {
        const value = obj[name];
        if (value && typeof value === 'object') {
            deepFreeze(value);
        }
    });
    return Object.freeze(obj);
}

// Sleep from: https://stackoverflow.com/a/39914235
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Spread the result of this to have items be conditionally present in an array.
 * Note: items are evaluated, regardless of the condition.
 * @param {any} condition
 * @param {...any} items
 * @returns {Array}
 */
export function conditionalArrayItem(condition, ...items) {
    return condition ? [].concat(items) : [];
}

/**
 * Spread the result of this to have an object key be conditionally present.
 * Note: items are evaluated, regardless of the condition.
 * @param {any} condition
 * @param {any} key
 * @param {any} value
 * @returns {Object}
 */
export function conditionalObjectItem(condition, key, value) {
    return condition ? { [key]: value } : {};
}

/**
 * Note: if using Javascript array.sort, undefined *always* sorts to the end.
 * @param {1|-1} emptyValueSortResult -1 (default) sorts empty values low, 1 sorts empty high
 * @param {1|-1} direction 1 (default) for ascending sort, -1 for descending
 * @returns {function}
 */
export function getSortComparator(emptyValueSortResult=-1, direction=1) {
    return (v1, v2) => {
        const isEmpty = (v) => v == null || Number.isNaN(v);
        if (isEmpty(v1) && isEmpty(v2)) return 0;
        if (isEmpty(v1)) return direction * emptyValueSortResult;
        if (isEmpty(v2)) return direction * -emptyValueSortResult;
        return direction * (v1 - v2);
    };
}

export function createUniqueKey() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Convert an index into a string label, like Excel columns:
 * A, B, C, ... Y, Z, AA, AB, ..., ZZ, AAA, ...
 * The default character set is A-Za-z0-9.
 * @param {number} index
 * @param {string} chars
 * @returns {string}
 */
export function getChainForIndex(
    index,
    chars='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
) {
    const len = chars.length;

    // This is based on the understanding that:
    // * the first len indices have 1 character (group 1)
    // * the next len*len indices have 2 characters (group 2)
    // * the next len**3 indices have 3 characters (group 3)
    // First, find the start and end indices for the group that target index falls into
    const bounds = [0, len];
    while (bounds[bounds.length-1] <= index) {
        bounds.push((len ** bounds.length) + bounds[bounds.length-1]);
    }
    const groupStart = bounds[bounds.length-2];
    const groupEnd = bounds[bounds.length-1];

    // With the start and end, we now have an offset to more easily assign the letters.
    const start = index - groupStart;
    const end = groupEnd - groupStart;
    const rs = [];
    for (let i = 1, working = start; i < end; i *= len) {
        const remainder = working % len;
        const div = Math.floor(working / len);
        rs.unshift(remainder);
        working = div;
    }
    return rs.map((r) => chars[r]).join('');
}

/**
 * Reverse of getChainForIndex: convert a string label back to a 0-based index.
 * Returns -1 if the label contains characters not in the provided set.
 * @param {string} label
 * @param {string} chars
 * @returns {number}
 */
export function getIndexForChain(
    label,
    chars='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
) {
    if (!label) return -1;
    const len = chars.length;
    const digits = label.split('').map((c) => chars.indexOf(c));
    if (digits.some((d) => d === -1)) return -1;

    // The group with n characters starts at: len + len^2 + ... + len^(n-1)
    // which equals len*(len^(n-1) - 1)/(len-1) for len > 1
    const n = digits.length;
    let groupStart = 0;
    for (let i = 1; i < n; i++) groupStart += len ** i;

    // Within the group, decode as a base-len number
    const offset = digits.reduce((acc, d) => acc * len + d, 0);
    return groupStart + offset;
}
